import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Alert,
  AlertTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Divider,
  Chip,
  Card,
  CardContent,
  Stack,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  DeleteOutline as DeleteIcon,
  SaveOutlined as SaveIcon,
  WarningAmber as WarningIcon,
  ErrorOutline as ErrorIcon,
  CheckCircleOutline as CheckCircleIcon,
  Download as DownloadIcon,
  InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { createProduct, getProductAttributes, getProducts, updateProduct } from '../../services/productService';
import { Unit, Product } from '../../types/product';
import { getUnits } from '../../services/productService';
import { getCategories } from '../../services/categoryService';
import { Category } from '../../types/category';
import axiosInstance from '../../utils/axiosConfig';

// واجهات لتمثيل البيانات المستوردة
interface SimpleProductImport {
  name: string;
  description?: string;
  sku?: string;
  barcode?: string;
  additionalBarcodes?: string[]; // إضافة دعم للباركودات الإضافية
  retailPrice?: number;
  wholesalePrice?: number;
  lastCost?: number; // إضافة حقل آخر تكلفة
  categoryId?: string;
  unit?: string;
  status?: 'active' | 'inactive' | 'discontinued';
  rowIndex?: number;
  isValid?: boolean;
  errors?: string[];
}

interface VariableProductImportVariant {
  sku?: string;
  barcode?: string;
  additionalBarcodes?: string[]; // إضافة دعم للباركودات الإضافية
  retailPrice?: number;
  wholesalePrice?: number;
  lastCost?: number; // إضافة حقل آخر تكلفة
  categoryId?: string;
  unit?: string;
  attributes: Record<string, string>;
  rowIndex?: number;
  isValid?: boolean;
  errors?: string[];
}

interface VariableProductImport {
  name: string;
  description?: string;
  status?: 'active' | 'inactive' | 'discontinued';
  attributeNames: string[];
  variants: VariableProductImportVariant[];
}

interface ColumnMapping {
  [key: string]: string;
}

// مكون عرض تقرير الخصائص
const AttributesValidationDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  report: any;
  onConfirm: () => void;
  loading: boolean;
}> = ({ open, onClose, report, onConfirm, loading }) => {
  if (!report) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <InfoIcon color="primary" />
          تقرير فحص الخصائص
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* ملخص الإحصائيات */}
        <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>ملخص الفحص</Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">إجمالي الخصائص</Typography>
                <Typography variant="h4">{report.summary.totalImportedAttributes}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="success.main">خصائص موجودة</Typography>
                <Typography variant="h4" color="success.main">{report.summary.existingCount}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="warning.main">خصائص جديدة</Typography>
                <Typography variant="h4" color="warning.main">{report.summary.newCount}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="info.main">قيم جديدة</Typography>
                <Typography variant="h4" color="info.main">{report.summary.newValuesCount}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* الخصائص الموجودة */}
        {report.existingAttributes.length > 0 && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" color="success.main">
                ✅ خصائص موجودة في النظام ({report.existingAttributes.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {report.existingAttributes.map((attr: any) => (
                <Box key={attr.name} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">{attr.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    القيم الموجودة: {attr.existingValues.join(', ')}
                  </Typography>
                  <Typography variant="body2" color="primary.main">
                    القيم في الملف: {attr.importedValues.join(', ')}
                  </Typography>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {/* الخصائص الجديدة */}
        {report.newAttributes.length > 0 && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" color="warning.main">
                ⚠️ خصائص جديدة سيتم إنشاؤها ({report.newAttributes.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {report.newAttributes.map((attr: any) => (
                <Box key={attr.name} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">{attr.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    القيم: {attr.values.join(', ')}
                  </Typography>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {/* القيم الجديدة للخصائص الموجودة */}
        {Object.keys(report.newValues).length > 0 && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" color="info.main">
                ℹ️ قيم جديدة للخصائص الموجودة ({Object.keys(report.newValues).length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {Object.entries(report.newValues).map(([attrName, valueInfo]: [string, any]) => (
                <Box key={attrName} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">{attrName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    القيم الجديدة: {valueInfo.newValues.join(', ')}
                  </Typography>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {/* الأخطاء */}
        {report.errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>أخطاء في الفحص</AlertTitle>
            {report.errors.map((error: string, index: number) => (
              <Typography key={index} variant="body2">{error}</Typography>
            ))}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          إلغاء
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
        >
          {loading ? 'جاري الإنشاء...' : 'موافق وإنشاء الخصائص'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProductImport: React.FC = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [productType, setProductType] = useState<'simple' | 'variable'>('simple');
  const [importMode, setImportMode] = useState<'create' | 'addVariants'>('create');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [units, setUnits] = useState<Unit[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [errors, setErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [importedProducts, setImportedProducts] = useState<(SimpleProductImport | VariableProductImport)[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [selectedColumnMapping, setSelectedColumnMapping] = useState<ColumnMapping>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // حالات جديدة لإدارة الخصائص
  const [attributesReport, setAttributesReport] = useState<any>(null);
  const [showAttributesDialog, setShowAttributesDialog] = useState<boolean>(false);
  const [attributesLoading, setAttributesLoading] = useState<boolean>(false);

  const requiredSimpleColumns = ['name', 'sku'];
  const requiredVariableColumns = ['name', 'attribute_*'];

  // تحميل الوحدات والتصنيفات عند بدء المكون
  useEffect(() => {
    const fetchData = async () => {
      try {
        // تحميل الوحدات
        const unitsResponse = await getUnits();
        setUnits(unitsResponse.units);
        
        // تحميل التصنيفات
        const categoriesResponse = await getCategories();
        setCategories(categoriesResponse);
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrors(prev => [...prev, 'فشل في تحميل البيانات الأساسية.']);
      }
    };
    
    fetchData();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleProductTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProductType(event.target.value as 'simple' | 'variable');
  };

  const handleImportModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setImportMode(event.target.value as 'create' | 'addVariants');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
      setErrors([]);
      setSuccessMessage('');
    }
  };

  const resetForm = () => {
    setFile(null);
    setImportedProducts([]);
    setErrors([]);
    setSuccessMessage('');
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const validateSimpleProduct = (product: SimpleProductImport, rowIndex: number): SimpleProductImport => {
    const errors: string[] = [];
    
    if (!product.name) {
      errors.push('اسم المنتج مطلوب');
    }
    
    if (!product.sku && !product.barcode) {
      errors.push('يجب توفير إما SKU أو الباركود');
    }
    
    // التحقق من السعر إذا تم توفيره
    if (product.retailPrice !== undefined && (isNaN(product.retailPrice) || product.retailPrice < 0)) {
      errors.push('سعر التجزئة يجب أن يكون رقمًا موجبًا');
    }
    
    if (product.wholesalePrice !== undefined && (isNaN(product.wholesalePrice) || product.wholesalePrice < 0)) {
      errors.push('سعر الجملة يجب أن يكون رقمًا موجبًا');
    }

    if (product.lastCost !== undefined && (isNaN(product.lastCost) || product.lastCost < 0)) {
      errors.push('آخر تكلفة يجب أن تكون رقمًا موجبًا');
    }
    
    // التحقق من الوحدة إذا تم توفيرها
    if (product.unit && !units.find(u => u.name === product.unit)) {
      errors.push(`الوحدة '${product.unit}' غير موجودة في قائمة الوحدات`);
    }
    
    // التحقق من التصنيف إذا تم توفيره
    if (product.categoryId && !categories.find(c => c._id === product.categoryId)) {
      errors.push(`التصنيف غير صالح`);
    }
    
    // التحقق من الحالة إذا تم توفيرها
    if (product.status && !['active', 'inactive', 'discontinued'].includes(product.status)) {
      errors.push('الحالة يجب أن تكون واحدة من: active، inactive، discontinued');
    }
    
    return {
      ...product,
      rowIndex,
      isValid: errors.length === 0,
      errors
    };
  };

  const validateVariableProduct = (product: VariableProductImport): VariableProductImport => {
    const validatedVariants = product.variants.map((variant, index) => {
      const errors: string[] = [];
      
      if (!variant.sku && !variant.barcode) {
        errors.push('يجب توفير إما SKU أو الباركود لكل متغير');
      }
      
      // التحقق من السعر إذا تم توفيره
      if (variant.retailPrice !== undefined && (isNaN(variant.retailPrice) || variant.retailPrice < 0)) {
        errors.push('سعر التجزئة يجب أن يكون رقمًا موجبًا');
      }
      
      if (variant.wholesalePrice !== undefined && (isNaN(variant.wholesalePrice) || variant.wholesalePrice < 0)) {
        errors.push('سعر الجملة يجب أن يكون رقمًا موجبًا');
      }

      if (variant.lastCost !== undefined && (isNaN(variant.lastCost) || variant.lastCost < 0)) {
        errors.push('آخر تكلفة يجب أن تكون رقمًا موجبًا');
      }
      
      // التحقق من الوحدة إذا تم توفيرها
      if (variant.unit && !units.find(u => u.name === variant.unit)) {
        errors.push(`الوحدة '${variant.unit}' غير موجودة في قائمة الوحدات`);
      }
      
      // التحقق من التصنيف إذا تم توفيره
      if (variant.categoryId && !categories.find(c => c._id === variant.categoryId)) {
        errors.push(`التصنيف غير صالح`);
      }
      
      // التحقق من قيم السمات
      for (const attrName of product.attributeNames) {
        if (!variant.attributes[attrName]) {
          errors.push(`السمة '${attrName}' مطلوبة لكل متغير`);
        }
      }
      
      return {
        ...variant,
        rowIndex: index,
        isValid: errors.length === 0,
        errors
      };
    });
    
    return {
      ...product,
      variants: validatedVariants
    };
  };

  const parseSimpleProducts = (data: any[]): SimpleProductImport[] => {
    return data.map((row, index) => {
      // معالجة الباركودات الإضافية
      let additionalBarcodes: string[] | undefined = undefined;
      
      console.log(`🔍 معالجة الصف ${index + 1}:`, {
        name: row.name,
        barcode: row.barcode,
        additionalBarcodes: row.additionalBarcodes,
        additionalBarcode1: row.additionalBarcode1,
        additionalBarcode2: row.additionalBarcode2,
        allKeys: Object.keys(row)
      });
      
      if (row.additionalBarcodes) {
        // يمكن أن تكون الباركودات الإضافية مفصولة بفواصل أو أن تكون مخزنة في أعمدة منفصلة
        if (typeof row.additionalBarcodes === 'string') {
          additionalBarcodes = row.additionalBarcodes.split(',').map((code: string) => code.trim()).filter((code: string) => code);
          console.log(`✅ تم العثور على additionalBarcodes كنص:`, additionalBarcodes);
        } else {
          additionalBarcodes = [row.additionalBarcodes.toString()];
          console.log(`✅ تم العثور على additionalBarcodes كقيمة:`, additionalBarcodes);
        }
      }
      
      // بحث عن أي أعمدة إضافية تحمل أسماء مثل additionalBarcode1, additionalBarcode2, ...إلخ
      const additionalBarcodeColumns = Object.keys(row).filter(key =>
        key.startsWith('additionalBarcode') && row[key] && key !== 'additionalBarcodes'
      );
      
      console.log(`🔍 أعمدة الباركود الإضافية الموجودة:`, additionalBarcodeColumns);
      
      if (additionalBarcodeColumns.length > 0) {
        if (!additionalBarcodes) {
          additionalBarcodes = [];
        }
        
        additionalBarcodeColumns.forEach(column => {
          const value = row[column];
          console.log(`🔍 فحص عمود ${column}: القيمة = ${value}, النوع = ${typeof value}`);
          
          if (value !== undefined && value !== null && value !== '') {
            const stringValue = value.toString().trim();
            if (stringValue) {
              additionalBarcodes!.push(stringValue);
              console.log(`✅ تمت إضافة باركود من ${column}:`, stringValue);
            }
          }
        });
      }
      
      console.log(`📋 الباركودات الإضافية النهائية للمنتج ${row.name}:`, additionalBarcodes);
      
      const product: SimpleProductImport = {
        name: row.name || '',
        description: row.description || '',
        sku: row.sku || undefined,
        barcode: row.barcode || undefined,
        additionalBarcodes: additionalBarcodes,
        retailPrice: row.retailPrice ? Number(row.retailPrice) : undefined,
        wholesalePrice: row.wholesalePrice ? Number(row.wholesalePrice) : undefined,
        lastCost: row.lastCost ? Number(row.lastCost) : undefined, // إضافة حقل آخر تكلفة
        categoryId: row.categoryId || undefined,
        unit: row.unit || 'قطعة',  // استخدام 'قطعة' كوحدة افتراضية
        status: (row.status?.toLowerCase() as 'active' | 'inactive' | 'discontinued') || 'active'
      };
      
      return validateSimpleProduct(product, index);
    });
  };

  const parseVariableProducts = (data: any[]): VariableProductImport[] => {
    // تجميع الصفوف حسب اسم المنتج
    const groupedProducts: Record<string, any[]> = {};
    
    data.forEach((row, index) => {
      if (!row.name) {
        return;
      }
      
      if (!groupedProducts[row.name]) {
        groupedProducts[row.name] = [];
      }
      groupedProducts[row.name].push(row);
    });
    
    return Object.entries(groupedProducts).map(([name, rows]) => {
      // استخراج السمات من أسماء الأعمدة
      const firstRow = rows[0];
      const attributeColumns = Object.keys(firstRow).filter(key => 
        key.startsWith('attribute_') && key !== 'rowIndex' && key !== 'isValid' && key !== 'errors'
      );
      
      const attributeNames = attributeColumns.map(col => col.replace('attribute_', ''));
      
      // تحويل الصفوف إلى متغيرات
      const variants = rows.map(row => {
        const attributes: Record<string, string> = {};
        attributeColumns.forEach(col => {
          const attrName = col.replace('attribute_', '');
          attributes[attrName] = row[col];
        });
        
        // معالجة الباركودات الإضافية
        let additionalBarcodes: string[] | undefined = undefined;
        
        console.log(`🔍 معالجة متغير للمنتج ${name}:`, {
          barcode: row.barcode,
          additionalBarcodes: row.additionalBarcodes,
          additionalBarcode1: row.additionalBarcode1,
          additionalBarcode2: row.additionalBarcode2
        });
        
        if (row.additionalBarcodes) {
          // يمكن أن تكون الباركودات الإضافية مفصولة بفواصل أو أن تكون مخزنة في أعمدة منفصلة
          if (typeof row.additionalBarcodes === 'string') {
            additionalBarcodes = row.additionalBarcodes.split(',').map((code: string) => code.trim()).filter((code: string) => code);
            console.log(`✅ متغير - تم العثور على additionalBarcodes كنص:`, additionalBarcodes);
          } else {
            additionalBarcodes = [row.additionalBarcodes.toString()];
            console.log(`✅ متغير - تم العثور على additionalBarcodes كقيمة:`, additionalBarcodes);
          }
        }
        
        // بحث عن أي أعمدة إضافية تحمل أسماء مثل additionalBarcode1, additionalBarcode2, ...إلخ
        const additionalBarcodeColumns = Object.keys(row).filter(key =>
          key.startsWith('additionalBarcode') && row[key] && key !== 'additionalBarcodes'
        );
        
        console.log(`🔍 متغير - أعمدة الباركود الإضافية:`, additionalBarcodeColumns);
        
        if (additionalBarcodeColumns.length > 0) {
          if (!additionalBarcodes) {
            additionalBarcodes = [];
          }
          
          additionalBarcodeColumns.forEach(column => {
            const value = row[column];
            console.log(`🔍 متغير - فحص عمود ${column}: القيمة = ${value}, النوع = ${typeof value}`);
            
            if (value !== undefined && value !== null && value !== '') {
              const stringValue = value.toString().trim();
              if (stringValue) {
                additionalBarcodes!.push(stringValue);
                console.log(`✅ متغير - تمت إضافة باركود من ${column}:`, stringValue);
              }
            }
          });
        }
        
        console.log(`📋 متغير - الباركودات الإضافية النهائية:`, additionalBarcodes);
        
        return {
          attributes,
          sku: row.sku || undefined,
          barcode: row.barcode || undefined,
          additionalBarcodes: additionalBarcodes,
          retailPrice: row.retailPrice ? Number(row.retailPrice) : undefined,
          wholesalePrice: row.wholesalePrice ? Number(row.wholesalePrice) : undefined,
          lastCost: row.lastCost ? Number(row.lastCost) : undefined, // إضافة حقل آخر تكلفة
          categoryId: row.categoryId || undefined,
          unit: row.unit || undefined
        };
      });
      
      return {
        name,
        description: firstRow.description || '',
        status: (firstRow.status?.toLowerCase() as 'active' | 'inactive' | 'discontinued') || 'active',
        attributeNames,
        variants
      };
    });
  };

  // دالة فحص الخصائص مع النظام الموجود
  const validateAttributesWithSystem = async (importedProducts: (SimpleProductImport | VariableProductImport)[]) => {
    const report = {
      existingAttributes: [] as any[],
      newAttributes: [] as any[],
      newValues: {} as any,
      errors: [] as string[],
      summary: {
        totalImportedAttributes: 0,
        existingCount: 0,
        newCount: 0,
        newValuesCount: 0
      }
    };
    
    try {
      // الحصول على الخصائص الموجودة في النظام
      const existingAttributes = await getProductAttributes();
      const existingAttrMap = new Map(
        existingAttributes.map((attr: any) => [attr.name, attr])
      );
      
      // جمع جميع الخصائص من ملف الاستيراد (مع إزالة التكرار الذكي)
      const importedAttributesMap = new Map();
      
      importedProducts.forEach(product => {
        if ('attributeNames' in product && product.attributeNames && product.attributeNames.length > 0) {
          product.attributeNames.forEach(attrName => {
            if (!importedAttributesMap.has(attrName)) {
              importedAttributesMap.set(attrName, new Set());
            }
            
            // جمع جميع القيم لهذه الخاصية من المتغيرات (مع تطبيع القيم وإزالة المسافات المتعددة)
            product.variants.forEach(variant => {
              if (variant.attributes && variant.attributes[attrName]) {
                const rawValue = variant.attributes[attrName];
                // تحويل القيمة إلى نص ثم تطبيع: إزالة المسافات الزائدة من البداية والنهاية + تحويل المسافات المتعددة إلى مسافة واحدة
                const normalizedValue = String(rawValue).trim().replace(/\s+/g, ' ');
                
                if (normalizedValue) {
                  // التحقق من عدم وجود القيمة مسبقاً (case-insensitive مع تطبيع المسافات)
                  const existingValues = Array.from(importedAttributesMap.get(attrName)) as string[];
                  const isDuplicate = existingValues.some((v: string) =>
                    v.toLowerCase().replace(/\s+/g, ' ') === normalizedValue.toLowerCase()
                  );
                  
                  // إضافة القيمة فقط إذا لم تكن موجودة في الملف
                  if (!isDuplicate) {
                    importedAttributesMap.get(attrName).add(normalizedValue);
                  }
                }
              }
            });
          });
        }
      });
      
      report.summary.totalImportedAttributes = importedAttributesMap.size;
      
      // فحص كل خاصية مستوردة
      importedAttributesMap.forEach((valuesSet, attrName) => {
        const values = Array.from(valuesSet) as string[];
        
        if (existingAttrMap.has(attrName)) {
          // الخاصية موجودة في النظام
          const existingAttr = existingAttrMap.get(attrName) as any;
          report.existingAttributes.push({
            name: attrName,
            existingValues: existingAttr.values || [],
            importedValues: values
          });
          report.summary.existingCount++;
          
          // فحص القيم الجديدة (مع تجاهل حالة الأحرف)
          const existingValues = existingAttr.values || [];
          // تحويل القيم الموجودة إلى أحرف صغيرة للمقارنة
          const existingValuesLower = existingValues.map((v: string) => v.toLowerCase().trim());
          // فلترة القيم الجديدة فقط (مع تجاهل حالة الأحرف والمسافات)
          const newValues = values.filter((v: string) => {
            const normalizedValue = v.toLowerCase().trim();
            return !existingValuesLower.includes(normalizedValue);
          });
          
          if (newValues.length > 0) {
            report.newValues[attrName] = {
              attributeId: existingAttr._id,
              newValues: newValues
            };
            report.summary.newValuesCount += newValues.length;
          }
        } else {
          // خاصية جديدة غير موجودة في النظام
          report.newAttributes.push({
            name: attrName,
            values: values
          });
          report.summary.newCount++;
        }
      });
      
      return report;
    } catch (error: any) {
      report.errors.push(`خطأ في فحص الخصائص: ${error.message}`);
      return report;
    }
  };

  // دالة إنشاء الخصائص والقيم الجديدة
  const createMissingAttributesAndValues = async (attributesReport: any) => {
    const results = {
      createdAttributes: [] as any[],
      updatedAttributes: [] as any[],
      errors: [] as string[],
      success: true
    };
    
    try {
      // إنشاء الخصائص الجديدة
      for (const newAttr of attributesReport.newAttributes) {
        try {
          const response = await axiosInstance.post('/product-attributes', {
            name: newAttr.name,
            values: newAttr.values
          });
          
          results.createdAttributes.push(response.data);
          console.log(`تم إنشاء الخاصية: ${newAttr.name}`);
        } catch (error: any) {
          const errorMsg = `فشل إنشاء الخاصية "${newAttr.name}": ${error.response?.data?.message || error.message}`;
          results.errors.push(errorMsg);
          results.success = false;
        }
      }
      
      // إضافة القيم الجديدة للخصائص الموجودة
      for (const [attrName, valueInfo] of Object.entries(attributesReport.newValues)) {
        try {
          // الحصول على الخاصية الحالية
          const currentAttrResponse = await axiosInstance.get(`/product-attributes/${(valueInfo as any).attributeId}`);
          const currentAttr = currentAttrResponse.data;
          
          // دمج القيم الجديدة مع الموجودة
          const existingValues = currentAttr.values || [];
          const newValues = (valueInfo as any).newValues || [];
          const combinedValues = existingValues.concat(newValues);
          const updatedValues = Array.from(new Set(combinedValues));
          
          const updateResponse = await axiosInstance.put(`/product-attributes/${(valueInfo as any).attributeId}`, {
            name: attrName,
            values: updatedValues
          });
          
          results.updatedAttributes.push(updateResponse.data);
          console.log(`تم تحديث الخاصية: ${attrName} بإضافة ${(valueInfo as any).newValues.length} قيمة جديدة`);
        } catch (error: any) {
          const errorMsg = `فشل تحديث الخاصية "${attrName}": ${error.response?.data?.message || error.message}`;
          results.errors.push(errorMsg);
          results.success = false;
        }
      }
      
      return results;
    } catch (error: any) {
      results.errors.push(`خطأ عام: ${error.message}`);
      results.success = false;
      return results;
    }
  };
  
  // دالة لتحويل البيانات إلى التنسيق المطلوب لـ API
  const convertToProductFormat = (product: SimpleProductImport | VariableProductImport): Omit<Product, '_id'> => {
    console.log(`🔄 تحويل المنتج إلى تنسيق API:`, product.name);
    
    if ('variants' in product) {
      // إذا كان المنتج متغيرًا، فقد يكون التنسيق قريبًا من المطلوب
      return {
        name: product.name,
        description: product.description || '',
        attributeNames: product.attributeNames.map(name => ({
          name,
          values: Array.from(new Set(product.variants.map(v => v.attributes[name])))
        })),
        variants: product.variants.map((v, index) => ({
          serialNumber: index + 1, // إضافة الرقم التسلسلي
          attributes: v.attributes,
          sku: v.sku,
          barcodes: v.barcode ? [{
            code: v.barcode,
            additionalCodes: v.additionalBarcodes || [], // إضافة الباركودات الإضافية
            isDefault: true,
            retailPrice: v.retailPrice || 0,
            wholesalePrice: v.wholesalePrice || 0,
            lastCost: v.lastCost || 0, // إضافة آخر تكلفة
            customPrice1: 0,
            customPrice2: 0,
            unit: v.unit || 'قطعة'
          }] : [],
          quantity: 0, // وضع الكمية صفر بشكل افتراضي
          unit: v.unit || 'قطعة',
          status: 'active',
          categories: v.categoryId ? [v.categoryId] : []
        })),
        status: product.status || 'active'
      };
    } else {
      // إذا كان المنتج بسيطًا، فيجب تحويله إلى منتج بمتغير واحد
      return {
        name: product.name,
        description: product.description || '',
        attributeNames: [],
        variants: [{
          serialNumber: 1, // إضافة الرقم التسلسلي للمنتج البسيط
          attributes: {},
          sku: product.sku,
          barcodes: product.barcode ? [{
            code: product.barcode,
            additionalCodes: product.additionalBarcodes || [], // إضافة الباركودات الإضافية
            isDefault: true,
            retailPrice: product.retailPrice || 0,
            wholesalePrice: product.wholesalePrice || 0,
            lastCost: product.lastCost || 0, // إضافة آخر تكلفة
            customPrice1: 0,
            customPrice2: 0,
            unit: product.unit || 'قطعة'
          }] : [],
          quantity: 0, // وضع الكمية صفر بشكل افتراضي
          unit: product.unit || 'قطعة',
          status: 'active',
          categories: product.categoryId ? [product.categoryId] : []
        }],
        status: product.status || 'active'
      };
    }
  };

  const downloadTemplate = () => {
    // إنشاء نموذج بناءً على نوع المنتج المحدد ووضع الاستيراد
    let data: any[] = [];
    
    if (importMode === 'addVariants') {
      // نموذج لإضافة متغيرات لمنتجات موجودة
      data = [
        {
          name: 'قميص',  // اسم المنتج الموجود (يجب أن يطابق تماماً)
          sku: 'SHIRT-XL-BLU',
          barcode: '1234567890130',
          additionalBarcode1: '1234567891111',
          lastCost: 25,
          retailPrice: 49.99,
          wholesalePrice: 39.99,
          categoryId: categories.length > 0 ? categories[0]._id : '',
          unit: 'قطعة',
          attribute_size: 'XL',  // متغير جديد
          attribute_color: 'أزرق'
        },
        {
          name: 'قميص',  // نفس اسم المنتج
          sku: 'SHIRT-XL-GRN',
          barcode: '1234567890131',
          lastCost: 25,
          retailPrice: 49.99,
          wholesalePrice: 39.99,
          categoryId: categories.length > 0 ? categories[0]._id : '',
          unit: 'قطعة',
          attribute_size: 'XL',  // متغير جديد
          attribute_color: 'أخضر'  // متغير جديد
        }
      ];
    } else if (productType === 'simple') {
      // نموذج للمنتجات البسيطة
      data = [
        {
          name: 'مثال منتج 1',
          description: 'وصف المنتج الأول',
          sku: 'SKU001',
          barcode: '1234567890123',
          additionalBarcode1: '1234567890555',
          additionalBarcode2: '1234567890666',
          lastCost: 50, // آخر تكلفة
          retailPrice: 99.99,
          wholesalePrice: 79.99,
          categoryId: categories.length > 0 ? categories[0]._id : '',
          unit: 'قطعة',
          status: 'active'
        },
        {
          name: 'مثال منتج 2',
          description: 'وصف المنتج الثاني',
          sku: 'SKU002',
          barcode: '1234567890124',
          additionalBarcodes: '1234567890777,1234567890888',  // يمكن أيضًا استخدام قائمة مفصولة بفواصل
          lastCost: 75, // آخر تكلفة
          retailPrice: 149.99,
          wholesalePrice: 129.99,
          categoryId: categories.length > 0 ? categories[0]._id : '',
          unit: 'قطعة',
          status: 'active'
        }
      ];
    } else {
      // نموذج للمنتجات المتغيرة
      data = [
        {
          name: 'قميص',
          description: 'قميص قطني',
          sku: 'SHIRT-S-BLU',
          barcode: '1234567890125',
          additionalBarcode1: '1234567890999',
          additionalBarcode2: '1234567891000',
          lastCost: 25, // آخر تكلفة
          retailPrice: 49.99,
          wholesalePrice: 39.99,
          categoryId: categories.length > 0 ? categories[0]._id : '',
          unit: 'قطعة',
          status: 'active',
          attribute_size: 'S',
          attribute_color: 'أزرق'
        },
        {
          name: 'قميص',
          description: 'قميص قطني',
          sku: 'SHIRT-M-BLU',
          barcode: '1234567890126',
          lastCost: 25, // آخر تكلفة
          retailPrice: 49.99,
          wholesalePrice: 39.99,
          categoryId: categories.length > 0 ? categories[0]._id : '',
          unit: 'قطعة',
          status: 'active',
          attribute_size: 'M',
          attribute_color: 'أزرق'
        },
        {
          name: 'قميص',
          description: 'قميص قطني',
          sku: 'SHIRT-L-BLU',
          barcode: '1234567890127',
          lastCost: 25, // آخر تكلفة
          retailPrice: 49.99,
          wholesalePrice: 39.99,
          categoryId: categories.length > 0 ? categories[0]._id : '',
          unit: 'قطعة',
          status: 'active',
          attribute_size: 'L',
          attribute_color: 'أزرق'
        },
        {
          name: 'قميص',
          description: 'قميص قطني',
          sku: 'SHIRT-S-RED',
          barcode: '1234567890128',
          lastCost: 25, // آخر تكلفة
          retailPrice: 49.99,
          wholesalePrice: 39.99,
          categoryId: categories.length > 0 ? categories[0]._id : '',
          unit: 'قطعة',
          status: 'active',
          attribute_size: 'S',
          attribute_color: 'أحمر'
        }
      ];
    }
    
    // إنشاء كتاب عمل Excel
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    
    // تنزيل الملف
    let fileName = 'product-import-template.xlsx';
    if (importMode === 'addVariants') {
      fileName = 'add-variants-template.xlsx';
    } else if (productType === 'simple') {
      fileName = 'simple-products-template.xlsx';
    } else {
      fileName = 'variable-products-template.xlsx';
    }
    XLSX.writeFile(wb, fileName);
  };

  const importProducts = async () => {
    if (!file) {
      setErrors(['الرجاء اختيار ملف أولاً.']);
      return;
    }
    
    setLoading(true);
    setProgress(10);
    setErrors([]);
    
    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          setProgress(30);
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // استخدام الورقة الأولى
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // تحويل البيانات إلى JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (jsonData.length === 0) {
            setErrors(['الملف لا يحتوي على بيانات.']);
            setLoading(false);
            return;
          }
          
          setProgress(50);
          
          let products: (SimpleProductImport | VariableProductImport)[] = [];
          
          // في وضع إضافة المتغيرات، نستخدم دائماً parseVariableProducts
          if (importMode === 'addVariants' || productType === 'variable') {
            products = parseVariableProducts(jsonData);
          } else {
            products = parseSimpleProducts(jsonData);
          }
          
          setProgress(70);
          
          // حفظ المنتجات أولاً
          setImportedProducts(products);
          
          // فحص الخصائص مع النظام الموجود (للمنتجات المتغيرة أو وضع إضافة المتغيرات)
          if ((importMode === 'addVariants' || productType === 'variable') && products.length > 0) {
            console.log('🔍 بدء فحص الخصائص...');
            console.log('📦 عدد المنتجات:', products.length);
            console.log('📦 المنتجات:', products);
            
            try {
              const attributesReport = await validateAttributesWithSystem(products);
              console.log('📊 تقرير الخصائص الكامل:', JSON.stringify(attributesReport, null, 2));
              setAttributesReport(attributesReport);
              
              // عرض النافذة دائماً للمنتجات المتغيرة (حتى لو لم تكن هناك خصائص)
              console.log('✅ عرض نافذة تقرير الخصائص');
              setShowAttributesDialog(true);
              setSuccessMessage(`تم تحليل ${products.length} منتج. يرجى مراجعة تقرير الخصائص.`);
              setProgress(100);
              setLoading(false);
              return; // الخروج من الدالة لمنع إيقاف التحميل في finally
            } catch (error) {
              console.error('❌ خطأ في فحص الخصائص:', error);
              setErrors(['حدث خطأ أثناء فحص الخصائص، ولكن تم تحليل المنتجات بنجاح.']);
            }
          } else {
            // منتجات بسيطة أو لا توجد منتجات
            console.log('ℹ️ منتجات بسيطة - لا حاجة لفحص الخصائص');
            if (products.length > 0) {
              setSuccessMessage(`تم استيراد ${products.length} منتج/منتجات بنجاح. الرجاء مراجعة البيانات قبل الحفظ.`);
            } else {
              setErrors(['لم يتم العثور على منتجات صالحة في الملف.']);
            }
          }
          
          setProgress(90);
          
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          setErrors(['حدث خطأ أثناء معالجة ملف Excel. تأكد من أن الملف بالتنسيق الصحيح.']);
        } finally {
          setProgress(100);
          setLoading(false);
        }
      };
      
      reader.onerror = () => {
        setErrors(['حدث خطأ أثناء قراءة الملف.']);
        setLoading(false);
      };
      
      reader.readAsArrayBuffer(file);
      
    } catch (error) {
      console.error('Error importing products:', error);
      setErrors(['حدث خطأ أثناء استيراد المنتجات.']);
      setLoading(false);
    }
  };

  const saveProducts = async () => {
    setLoading(true);
    setProgress(10);
    setErrors([]);
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      if (importMode === 'addVariants') {
        // وضع إضافة متغيرات لمنتجات موجودة
        for (let i = 0; i < importedProducts.length; i++) {
          const product = importedProducts[i] as VariableProductImport;
          
          setProgress(10 + Math.floor((i / importedProducts.length) * 80));
          
          // تأخير 500 ميلي ثانية بين كل منتج لتجنب تجاوز حد الطلبات (Rate Limiting)
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          try {
            // البحث عن المنتج الموجود بالاسم
            const searchResult = await getProducts(1, -1, product.name);
            const existingProduct = searchResult.products.find(p => p.name === product.name);
            
            if (!existingProduct) {
              failCount++;
              setErrors(prevErrors => [...prevErrors, `المنتج "${product.name}" غير موجود في النظام`]);
              continue;
            }
            
            // التحقق من عدم تكرار المتغيرات
            const existingVariants = existingProduct.variants || [];
            const newVariants = product.variants.map((v, index) => {
              // التحقق من أن المتغير الجديد لا يتطابق مع متغير موجود
              const isDuplicate = existingVariants.some(existingVar => {
                // مقارنة الخصائص
                const attributesMatch = product.attributeNames.every(attrName => {
                  return existingVar.attributes && existingVar.attributes[attrName] === v.attributes[attrName];
                });
                return attributesMatch;
              });
              
              if (isDuplicate) {
                throw new Error(`المتغير بالخصائص ${JSON.stringify(v.attributes)} موجود مسبقاً`);
              }
              
              // إنشاء رقم تسلسلي جديد
              const maxSerialNumber = Math.max(0, ...existingVariants.map(ev => ev.serialNumber || 0));
              
              return {
                serialNumber: maxSerialNumber + index + 1,
                attributes: v.attributes,
                sku: v.sku,
                barcodes: v.barcode ? [{
                  code: v.barcode,
                  additionalCodes: v.additionalBarcodes || [],
                  isDefault: true,
                  retailPrice: v.retailPrice || 0,
                  wholesalePrice: v.wholesalePrice || 0,
                  lastCost: v.lastCost || 0,
                  customPrice1: 0,
                  customPrice2: 0,
                  unit: v.unit || 'قطعة'
                }] : [],
                quantity: 0,
                unit: v.unit || 'قطعة',
                status: 'active' as 'active' | 'inactive' | 'outOfStock',
                categories: v.categoryId ? [v.categoryId] : []
              };
            });
            
            // دمج المتغيرات الجديدة مع الموجودة
            const updatedProduct = {
              ...existingProduct,
              variants: [...existingVariants, ...newVariants]
            };
            
            // تحديث المنتج
            if (existingProduct._id) {
              await updateProduct(existingProduct._id, updatedProduct);
              successCount++;
            } else {
              throw new Error('معرف المنتج غير موجود');
            }
          } catch (error: any) {
            console.error('Error adding variants:', error);
            failCount++;
            
            let errorMessage = `المنتج ${i + 1}: `;
            if (error?.response?.data?.error) {
              errorMessage += error.response.data.error;
            } else if (error?.response?.data?.message) {
              errorMessage += error.response.data.message;
            } else if (error?.message) {
              errorMessage += error.message;
            } else {
              errorMessage += 'خطأ غير معروف';
            }
            
            setErrors(prevErrors => [...prevErrors, errorMessage]);
          }
        }
      } else {
        // وضع إنشاء منتجات جديدة (الوضع الأصلي)
        for (let i = 0; i < importedProducts.length; i++) {
          const product = importedProducts[i];
          const formattedProduct = convertToProductFormat(product);
          
          setProgress(10 + Math.floor((i / importedProducts.length) * 80));
          
          // تأخير 500 ميلي ثانية بين كل منتج لتجنب تجاوز حد الطلبات (Rate Limiting)
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          try {
            await createProduct(formattedProduct);
            successCount++;
          } catch (error: any) {
            console.error('Error saving product:', error);
            failCount++;
            
            // إضافة تفاصيل الخطأ للعرض
            let errorMessage = `المنتج ${i + 1}: `;
            
            // معالجة أخطاء الباركود المكرر
            if (error.type === 'barcode_duplicate' || error.type === 'barcode_validation') {
              if (error.details && Array.isArray(error.details)) {
                errorMessage += error.details.join(', ');
              } else {
                errorMessage += error.message || 'يوجد باركود مكرر';
              }
            }
            // معالجة الأخطاء العامة
            else if (error?.response?.data?.error) {
              errorMessage += error.response.data.error;
            }
            else if (error?.response?.data?.message) {
              errorMessage += error.response.data.message;
            }
            else if (error?.message) {
              errorMessage += error.message;
            }
            else {
              errorMessage += 'خطأ غير معروف';
            }
            
            // إضافة الخطأ لقائمة الأخطاء
            setErrors(prevErrors => [...prevErrors, errorMessage]);
          }
        }
      }
      
      if (successCount > 0) {
        setSuccessMessage(`تم حفظ ${successCount} منتج/منتجات بنجاح. ${failCount > 0 ? `فشل حفظ ${failCount} منتج/منتجات.` : ''}`);
      } else {
        setErrors(['فشل في حفظ المنتجات. الرجاء المحاولة مرة أخرى.']);
      }
      
      // إعادة تعيين القائمة بعد الحفظ بنجاح
      if (successCount > 0) {
        setImportedProducts([]);
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
      
    } catch (error) {
      console.error('Error saving products:', error);
      setErrors(['حدث خطأ أثناء حفظ المنتجات.']);
    } finally {
      setProgress(100);
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  // دالة تأكيد إنشاء الخصائص
  const confirmAttributesCreation = async () => {
    setAttributesLoading(true);
    setProgress(10);
    
    try {
      // إنشاء الخصائص والقيم المفقودة
      const creationResults = await createMissingAttributesAndValues(attributesReport);
      
      setProgress(80);
      
      if (!creationResults.success) {
        setErrors(creationResults.errors);
        return;
      }
      
      // إظهار نتائج الإنشاء
      const successMessages: string[] = [];
      if (creationResults.createdAttributes.length > 0) {
        successMessages.push(`تم إنشاء ${creationResults.createdAttributes.length} خاصية جديدة`);
      }
      if (creationResults.updatedAttributes.length > 0) {
        successMessages.push(`تم تحديث ${creationResults.updatedAttributes.length} خاصية موجودة`);
      }
      
      setSuccessMessage(successMessages.join(' و '));
      setShowAttributesDialog(false);
      setProgress(100);
      
      // الآن يمكن متابعة عملية حفظ المنتجات
      
    } catch (error: any) {
      console.error('Error creating attributes:', error);
      setErrors(['فشل في إنشاء الخصائص']);
    } finally {
      setAttributesLoading(false);
    }
  };

  const confirmSave = () => {
    setShowConfirmation(true);
  };

  const renderSimpleProductTable = () => {
    const simpleProducts = importedProducts as SimpleProductImport[];
    
    return (
      <TableContainer component={Paper} sx={{ mt: 3, mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>الحالة</TableCell>
              <TableCell>اسم المنتج</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>الباركود</TableCell>
              <TableCell>الباركودات الإضافية</TableCell>
              <TableCell>سعر التجزئة</TableCell>
              <TableCell>سعر الجملة</TableCell>
              <TableCell>آخر تكلفة</TableCell>
              <TableCell>التصنيف</TableCell>
              <TableCell>الوحدة</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>أخطاء</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {simpleProducts
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((product, index) => (
                <TableRow 
                  key={index}
                  sx={{ backgroundColor: product.isValid ? 'transparent' : 'rgba(255, 0, 0, 0.05)' }}
                >
                  <TableCell>
                    {product.isValid ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.sku || '-'}</TableCell>
                  <TableCell>{product.barcode || '-'}</TableCell>
                  <TableCell>
                    {product.additionalBarcodes && product.additionalBarcodes.length > 0
                      ? product.additionalBarcodes.join(', ')
                      : '-'}
                  </TableCell>
                  <TableCell>{product.retailPrice !== undefined ? product.retailPrice : '-'}</TableCell>
                  <TableCell>{product.wholesalePrice !== undefined ? product.wholesalePrice : '-'}</TableCell>
                  <TableCell>{product.lastCost !== undefined ? product.lastCost : '-'}</TableCell>
                  <TableCell>
                    {product.categoryId 
                      ? categories.find(c => c._id === product.categoryId)?.name || product.categoryId 
                      : '-'}
                  </TableCell>
                  <TableCell>{product.unit || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={product.status || 'active'} 
                      size="small" 
                      color={product.status === 'active' ? 'success' : product.status === 'inactive' ? 'default' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    {product.errors && product.errors.length > 0 ? (
                      <Tooltip title={product.errors.join('\n')} arrow>
                        <IconButton size="small" color="error">
                          <WarningIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={simpleProducts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="صفوف في الصفحة:"
        />
      </TableContainer>
    );
  };

  const renderVariableProductTable = () => {
    const variableProducts = importedProducts as VariableProductImport[];
    
    return (
      <>
        {variableProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((product, productIndex) => (
          <Card key={productIndex} sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6">{product.name}</Typography>
                    <Chip 
                      label={product.status || 'active'} 
                      size="small" 
                      color={product.status === 'active' ? 'success' : product.status === 'inactive' ? 'default' : 'error'}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {product.description}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    السمات: {product.attributeNames.join(', ')}
                  </Typography>
                  
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>الحالة</TableCell>
                          <TableCell>SKU</TableCell>
                          <TableCell>الباركود</TableCell>
                          <TableCell>الباركودات الإضافية</TableCell>
                          {product.attributeNames.map(attr => (
                            <TableCell key={attr}>{attr}</TableCell>
                          ))}
                          <TableCell>سعر التجزئة</TableCell>
                          <TableCell>سعر الجملة</TableCell>
                          <TableCell>آخر تكلفة</TableCell>
                          <TableCell>التصنيف</TableCell>
                          <TableCell>الوحدة</TableCell>
                          <TableCell>أخطاء</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {product.variants.map((variant, variantIndex) => (
                          <TableRow 
                            key={variantIndex}
                            sx={{ backgroundColor: variant.isValid ? 'transparent' : 'rgba(255, 0, 0, 0.05)' }}
                          >
                            <TableCell>
                              {variant.isValid ? (
                                <CheckCircleIcon color="success" fontSize="small" />
                              ) : (
                                <ErrorIcon color="error" fontSize="small" />
                              )}
                            </TableCell>
                            <TableCell>{variant.sku || '-'}</TableCell>
                            <TableCell>{variant.barcode || '-'}</TableCell>
                            <TableCell>
                              {variant.additionalBarcodes && variant.additionalBarcodes.length > 0
                                ? variant.additionalBarcodes.join(', ')
                                : '-'}
                            </TableCell>
                            {product.attributeNames.map(attr => (
                              <TableCell key={attr}>{variant.attributes[attr] || '-'}</TableCell>
                            ))}
                            <TableCell>{variant.retailPrice !== undefined ? variant.retailPrice : '-'}</TableCell>
                            <TableCell>{variant.wholesalePrice !== undefined ? variant.wholesalePrice : '-'}</TableCell>
                            <TableCell>{variant.lastCost !== undefined ? variant.lastCost : '-'}</TableCell>
                            <TableCell>
                              {variant.categoryId 
                                ? categories.find(c => c._id === variant.categoryId)?.name || variant.categoryId 
                                : '-'}
                            </TableCell>
                            <TableCell>{variant.unit || '-'}</TableCell>
                            <TableCell>
                              {variant.errors && variant.errors.length > 0 ? (
                                <Tooltip title={variant.errors.join('\n')} arrow>
                                  <IconButton size="small" color="error">
                                    <WarningIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
        
        <TablePagination
          rowsPerPageOptions={[1, 2, 5, 10]}
          component="div"
          count={variableProducts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="منتجات في الصفحة:"
        />
      </>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        استيراد المنتجات
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        قم بتحميل ملف إكسل يحتوي على بيانات المنتجات التي ترغب في استيرادها.
      </Typography>
      
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="استيراد المنتجات" />
        <Tab label="المنتجات المستوردة" disabled={importedProducts.length === 0} />
      </Tabs>
      
      {activeTab === 0 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              وضع الاستيراد
            </Typography>
            
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">اختر وضع الاستيراد</FormLabel>
              <RadioGroup
                row
                name="import-mode"
                value={importMode}
                onChange={handleImportModeChange}
              >
                <FormControlLabel value="create" control={<Radio />} label="إنشاء منتجات جديدة" />
                <FormControlLabel value="addVariants" control={<Radio />} label="إضافة متغيرات لمنتجات موجودة" />
              </RadioGroup>
            </FormControl>

            {importMode === 'create' && (
              <>
                <Typography variant="h6" gutterBottom>
                  اختر نوع المنتج
                </Typography>
                
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    name="product-type"
                    value={productType}
                    onChange={handleProductTypeChange}
                  >
                    <FormControlLabel value="simple" control={<Radio />} label="منتج بسيط" />
                    <FormControlLabel value="variable" control={<Radio />} label="منتج متغير" />
                  </RadioGroup>
                </FormControl>
              </>
            )}

            {importMode === 'addVariants' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                في هذا الوضع، سيتم إضافة متغيرات جديدة للمنتجات الموجودة بناءً على اسم المنتج في ملف Excel.
                تأكد من أن اسم المنتج في الملف يطابق تماماً اسم المنتج الموجود في النظام.
              </Alert>
            )}
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                حمّل نموذج:
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                size="small"
              >
                تنزيل نموذج Excel {
                  importMode === 'addVariants'
                    ? 'لإضافة متغيرات'
                    : productType === 'simple'
                      ? 'للمنتجات البسيطة'
                      : 'للمنتجات المتغيرة'
                }
              </Button>
            </Box>
          </Paper>
          
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              تحميل ملف إكسل
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadIcon />}
                sx={{ mb: 2 }}
              >
                اختر ملف
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
              </Button>
              
              {file && (
                <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
                  تم اختيار الملف: {file.name}
                </Alert>
              )}
              
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={importProducts}
                  disabled={!file || loading}
                >
                  استيراد
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={resetForm}
                  disabled={loading}
                >
                  إعادة تعيين
                </Button>
              </Box>
            </Box>
            
            {loading && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  جاري المعالجة... {progress}%
                </Typography>
              </Box>
            )}
            
            {errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <div>
                  {errors.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              </Alert>
            )}
            
            {successMessage && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {successMessage}
              </Alert>
            )}
          </Paper>
          
          {importedProducts.length > 0 && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={confirmSave}
                disabled={loading}
              >
                حفظ المنتجات ({importedProducts.length})
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => setActiveTab(1)}
              >
                عرض المنتجات المستوردة
              </Button>
            </Box>
          )}
        </Box>
      )}
      
      {activeTab === 1 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                المنتجات المستوردة ({importedProducts.length})
              </Typography>
              
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={confirmSave}
                disabled={loading}
              >
                حفظ المنتجات
              </Button>
            </Box>
            
            {loading && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  جاري الحفظ... {progress}%
                </Typography>
              </Box>
            )}
            
            {errors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <div>
                  {errors.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              </Alert>
            )}
            
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}
            
            {(importMode === 'addVariants' || productType === 'variable') ? renderVariableProductTable() : renderSimpleProductTable()}
          </Paper>
        </Box>
      )}
      
      <Dialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
      >
        <DialogTitle>تأكيد الحفظ</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من حفظ {importedProducts.length} منتج/منتجات إلى قاعدة البيانات؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmation(false)} color="inherit">
            إلغاء
          </Button>
          <Button onClick={saveProducts} color="primary" variant="contained" autoFocus>
            نعم، حفظ المنتجات
          </Button>
        </DialogActions>
      </Dialog>

      {/* مكون تقرير الخصائص */}
      <AttributesValidationDialog
        open={showAttributesDialog}
        onClose={() => setShowAttributesDialog(false)}
        report={attributesReport}
        onConfirm={confirmAttributesCreation}
        loading={attributesLoading}
      />
    </Box>
  );
};

export default ProductImport;